import { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { spRequest } from '../authConfig';
import { getWins, getUserLikes, addLike, removeLike, deleteWin, getAllLikeCounts } from '../graphService';
import WinCard from './WinCard';
import WeeklySummary from './WeeklySummary';
import WinDetailModal from './WinDetailModal';
import EditWinModal from './EditWinModal';
import '../styles/WinsGallery.css';

export default function WinsGallery({ siteId, listId }) {
  const { instance, accounts } = useMsal();
  const [wins, setWins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWin, setSelectedWin] = useState(null);
  const [editingWin, setEditingWin] = useState(null);
  const currentUser = accounts[0]?.name || '';
  const localLikesKey = `ai-wins-likes:${accounts[0]?.username || ''}`;

  // Filters
  const [deptFilter, setDeptFilter] = useState('All');
  const [submitterFilter, setSubmitterFilter] = useState('All');
  const [likedFilter, setLikedFilter] = useState(false);

  const getLocalLikes = () => {
    try { return new Set(JSON.parse(localStorage.getItem(localLikesKey)) || []); }
    catch { return new Set(); }
  };

  const saveLocalLikes = (ids) => {
    localStorage.setItem(localLikesKey, JSON.stringify([...ids]));
  };

  const [likedIds, setLikedIds] = useState(getLocalLikes);
  const [likeCounts, setLikeCounts] = useState({});

  const fetchLikes = useCallback(async () => {
    try {
      const tokenRes = await instance.acquireTokenSilent({ ...spRequest, account: accounts[0] });
      const [ids, counts] = await Promise.all([
        getUserLikes(tokenRes.accessToken),
        getAllLikeCounts(tokenRes.accessToken),
      ]);
      setLikedIds(ids);
      saveLocalLikes(ids);
      setLikeCounts(counts);
    } catch {
      // SharePoint list not yet available — keep localStorage state
    }
  }, [instance, accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLikes(); }, [fetchLikes]);

  const toggleLike = async (winId) => {
    const id = String(winId);
    const isLiked = likedIds.has(id);
    const next = new Set(likedIds);
    isLiked ? next.delete(id) : next.add(id);
    setLikedIds(next);
    saveLocalLikes(next);
    setLikeCounts((prev) => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) + (isLiked ? -1 : 1)) }));
    try {
      const tokenRes = await instance.acquireTokenSilent({ ...spRequest, account: accounts[0] });
      isLiked ? await removeLike(tokenRes.accessToken, id) : await addLike(tokenRes.accessToken, id);
    } catch { /* list not set up yet */ }
  };

  const fetchWins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tokenRes = await instance.acquireTokenSilent({ ...spRequest, account: accounts[0] });
      const data = await getWins(tokenRes.accessToken, siteId, listId);
      setWins(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, siteId, listId]);

  useEffect(() => { fetchWins(); }, [fetchWins]);

  // Build filter options from data
  const departments = ['All', ...Array.from(
    new Set(wins.map((w) => w.department).filter(Boolean))
  ).sort()];

  const submitters = ['All', 'My Wins', ...Array.from(
    new Set(wins.map((w) => w.onBehalfOf || w.submittedBy).filter(Boolean))
  ).sort()];

  // Apply filters
  const filteredWins = wins.filter((win) => {
    if (deptFilter !== 'All' && win.department !== deptFilter) return false;
    if (submitterFilter === 'My Wins') {
      const owner = win.onBehalfOf || win.submittedBy;
      if (owner !== currentUser) return false;
    } else if (submitterFilter !== 'All') {
      const owner = win.onBehalfOf || win.submittedBy;
      if (owner !== submitterFilter) return false;
    }
    if (likedFilter && !likedIds.has(String(win.id))) return false;
    return true;
  });

  const activeFilterCount = (deptFilter !== 'All' ? 1 : 0) + (submitterFilter !== 'All' ? 1 : 0) + (likedFilter ? 1 : 0);

  return (
    <div className="gallery-container">
      <div className="gallery-header">
        <h2 className="gallery-heading">AI Wins at Litehouse Foods</h2>
        <p className="gallery-subheading">See how our team is using AI to make an impact every day.</p>
      </div>

      {!loading && !error && <WeeklySummary wins={wins} />}

      {!loading && !error && wins.length > 0 && (
        <div className="gallery-filters">
          <div className="filter-group">
            <label className="filter-label">Department</label>
            <select className="dept-filter" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Submitted By</label>
            <select className="dept-filter" value={submitterFilter} onChange={(e) => setSubmitterFilter(e.target.value)}>
              {submitters.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Liked</label>
            <button
              className={`liked-filter-btn ${likedFilter ? 'active' : ''}`}
              onClick={() => setLikedFilter((v) => !v)}
              title="Show only wins you've liked"
            >
              {likedFilter ? '♥ My Likes' : '♡ My Likes'}
            </button>
          </div>

          {activeFilterCount > 0 && (
            <button className="filter-clear" onClick={() => { setDeptFilter('All'); setSubmitterFilter('All'); setLikedFilter(false); }}>
              Clear filters
            </button>
          )}
        </div>
      )}

      {loading && <div className="gallery-loading">Loading wins...</div>}
      {error && <div className="gallery-error">Error loading wins: {error}</div>}

      {!loading && !error && filteredWins.length === 0 && (
        <div className="gallery-empty">
          <p>{activeFilterCount > 0 ? 'No wins match your filters.' : 'No wins submitted yet. Be the first!'}</p>
        </div>
      )}

      {!loading && !error && filteredWins.length > 0 && (
        <div className="wins-grid">
          {filteredWins.map((win) => (
            <WinCard
              key={win.id}
              win={win}
              onClick={() => setSelectedWin(win)}
              liked={likedIds.has(String(win.id))}
              likeCount={likeCounts[String(win.id)] || 0}
              onLike={(e) => { e.stopPropagation(); toggleLike(win.id); }}
            />
          ))}
        </div>
      )}

      {selectedWin && !editingWin && (
        <WinDetailModal
          win={selectedWin}
          onClose={() => setSelectedWin(null)}
          currentUser={currentUser}
          onEdit={() => setEditingWin(selectedWin)}
          onDelete={async (id) => {
            const tokenRes = await instance.acquireTokenSilent({ ...spRequest, account: accounts[0] });
            await deleteWin(tokenRes.accessToken, id);
            setWins((prev) => prev.filter((w) => w.id !== id));
            setSelectedWin(null);
          }}
        />
      )}

      {editingWin && (
        <EditWinModal
          win={editingWin}
          onClose={() => setEditingWin(null)}
          onSaved={(updated) => {
            setWins((prev) => prev.map((w) => w.id === updated.id ? updated : w));
            setSelectedWin(updated);
            setEditingWin(null);
          }}
        />
      )}
    </div>
  );
}
